import React, { useEffect, useState } from "react";
import { PageHeader, Section } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { RefreshCw, Plus, Loader2 } from "lucide-react";

const SSL_TYPES = [
  "PositiveSSL",
  "EssentialSSL",
  "InstantSSL",
  "PremiumSSL",
  "EV SSL",
  "PositiveSSL Wildcard",
  "EssentialSSL Wildcard",
];

const SSL = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [years, setYears] = useState(1);
  const [typeName, setTypeName] = useState("PositiveSSL");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ssl/list");
      setCerts(data.certs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/ssl/create", { years, type_name: typeName });
      toast.success("SSL purchase initiated");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Security"
        title="SSL Certificates"
        description="Manage SSL certificates owned by your Namecheap account."
        actions={
          <>
            <Button
              data-testid="btn-refresh-ssl"
              onClick={load}
              variant="outline"
              className="rounded-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              data-testid="btn-new-ssl"
              onClick={() => setOpen(true)}
              className="brand-bg hover:brand-bg text-white rounded-sm"
            >
              <Plus className="w-4 h-4 mr-2" /> Purchase SSL
            </Button>
          </>
        }
      />
      <Section>
        <div className="border border-[var(--border)] rounded-sm bg-white overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)] border-b border-[var(--border)] bg-[#FAFAFA]">
            <div className="col-span-2">CertId</div>
            <div className="col-span-4">Type</div>
            <div className="col-span-3">Host</div>
            <div className="col-span-2">Expires</div>
            <div className="col-span-1">Status</div>
          </div>
          {loading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : certs.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--text-secondary)]">No SSL certificates.</div>
          ) : (
            <div className="divide-y divide-[var(--border)]" data-testid="ssl-list">
              {certs.map((c, i) => (
                <div key={i} className="grid grid-cols-12 items-center px-4 py-3 dense-row text-sm">
                  <div className="col-span-2 mono">{c.CertificateID}</div>
                  <div className="col-span-4">{c.SSLType}</div>
                  <div className="col-span-3 mono text-xs">{c.HostName || "—"}</div>
                  <div className="col-span-2 mono text-xs text-[var(--text-secondary)]">{c.ExpiresDate}</div>
                  <div className="col-span-1">
                    <Badge variant="outline" className="rounded-sm uppercase text-[10px]">
                      {c.Status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Purchase SSL Certificate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Certificate Type</Label>
              <Select value={typeName} onValueChange={setTypeName}>
                <SelectTrigger data-testid="ssl-type" className="rounded-sm mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SSL_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Years</Label>
              <Input
                data-testid="ssl-years"
                type="number"
                min={1}
                max={5}
                value={years}
                onChange={(e) => setYears(parseInt(e.target.value) || 1)}
                className="rounded-sm mt-1.5 mono"
              />
            </div>
            <p className="text-xs text-[var(--text-secondary)]">
              This creates a new certificate order in your Namecheap account. Activation is a separate step in Namecheap dashboard.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              data-testid="btn-confirm-ssl"
              onClick={create}
              disabled={busy}
              className="brand-bg hover:brand-bg text-white rounded-sm"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Purchase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SSL;
